import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import formatEuro from '../../helpers/formatEuro'
import notAttributedText from '../../helpers/msiMonetisationHelpers/notAttributedText'
import { resolveProductOpening } from '../../helpers/impactInBeeld/resolveProductOpening'

// Volledige CF-URL — de nette /api/impactpage-rewrite staat in firebase.json
// maar wordt pas actief bij de eerstvolgende hosting-release (wij deployen
// nooit hosting); tot die tijd werkt de directe URL in elke omgeving.
const PUBLIC_IMPACT_ENDPOINT = 'https://europe-west1-deccos-app.cloudfunctions.net/getPublicImpactPage'

// Kassascherm-polling: live genoeg voor een scherm bij de kassa, zonder
// realtime-listener (de Cloud Function blijft de enige publieke deur).
const LIVE_POLL_MS = 15000

/**
 * @typedef {object} ImpactPageData
 * @property {object} organisation - Org-laag: kengetal, label, jaar, totalValue, branding, publishedAtMs
 * @property {object|null} product - Product-laag (bij een product-URL): name, price, impact, openingLine, microStory
 * @property {object|null} live - Live-totalen (fase 3): transactionCount, revenue, value, lastTransactionAtMs
 */

/**
 * Publieke impactpagina (QR-landingspagina) op /:client/impactopen/:slug?.
 * Toont de laatst GEPUBLICEERDE snapshot — trechter product → organisatie —
 * plus de live-totalen zodra er transacties binnenkomen. `?live=1`
 * (kassascherm) pollt elke 15 s; `?embed=1` rendert compact voor iframes.
 * Zonder publicatie: eerlijke 404-staat.
 * See docs/features/impact-in-beeld-fase3.md.
 */
const ImpactPageOpen = () => {
    const location = useLocation()

    const segments = location.pathname.split('/')
    const client = segments[1]
    const slug = segments[3] || ''
    const searchParams = new URLSearchParams(location.search)
    const isScan = searchParams.get('scan') === '1'
    const isLive = searchParams.get('live') === '1'
    const isEmbed = searchParams.get('embed') === '1'

    const [data, setData] = useState(/** @type {ImpactPageData | null} */ (null))
    const [status, setStatus] = useState('loading')

    // quiet = poll-modus: een mislukte poll laat de laatste cijfers staan
    // (stille retry bij de volgende tick) — een kassascherm mag niet flitsen.
    const fetchPage = useCallback(async (quiet, scanFlag) => {
        try {
            const res = await fetch(PUBLIC_IMPACT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ client, slug: slug || undefined, scan: scanFlag }),
            })

            if (res.ok) {
                const payload = await res.json()
                setData(payload)
                setStatus('ok')
            } else if (!quiet) {
                setStatus(res.status === 404 ? 'notfound' : 'error')
            }
        } catch (error) {
            if (!quiet) setStatus('error')
        }
    }, [client, slug])

    useEffect(() => {
        let cancelled = false

        const initialFetch = async () => {
            if (!cancelled) await fetchPage(false, isScan)
        }
        initialFetch()

        return () => { cancelled = true }
        // isScan bewust buiten de deps: een latere query-wijziging mag geen
        // tweede fetch (en dus dubbele scan-telling) veroorzaken.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchPage])

    useEffect(() => {
        // Ook pollen in de error-staat: een onbemand kassascherm moet na een
        // transiene storing vanzelf herstellen (quiet=false zet 'ok' terug).
        if (!isLive || status === 'loading' || status === 'notfound') return undefined

        const interval = setInterval(() => {
            // Vergeten achtergrond-tabs niet laten meepollen.
            if (document.hidden) return
            // Polls tellen nooit als scan.
            fetchPage(status === 'error' ? false : true, false)
        }, LIVE_POLL_MS)
        return () => clearInterval(interval)
    }, [isLive, status, fetchPage])

    if (status === 'loading') return null

    if (status === 'notfound') {
        return (
            <div className='impact-page-open impact-page-open--message'>
                <h1>Nog geen gepubliceerde impactpagina</h1>
                <p>Deze organisatie heeft deze pagina (nog) niet gepubliceerd.</p>
            </div>
        )
    }

    if (status === 'error' || !data) {
        return (
            <div className='impact-page-open impact-page-open--message'>
                <h1>Er ging iets mis bij het laden van deze pagina.</h1>
                <p>Probeer het later opnieuw.</p>
            </div>
        )
    }

    const { organisation, product, live } = data
    const branding = organisation.branding || {}
    const publishedAtMs = product?.publishedAtMs || organisation.publishedAtMs

    const liveSection = live && (
        <section className='impact-page-open-section'>
            <h2>Samen geteld</h2>
            <div className='impact-page-open-stats'>
                <div className='impact-page-open-stat'>
                    <p className='impact-page-open-stat-value' style={{ color: branding.primaryColor || '#47acc3' }}>
                        {live.transactionCount}
                    </p>
                    <p className='impact-page-open-stat-label'>afrekeningen geteld</p>
                </div>
                <div className='impact-page-open-stat'>
                    <p className='impact-page-open-stat-value' style={{ color: branding.primaryColor || '#47acc3' }}>
                        {formatEuro(live.value)}
                    </p>
                    <p className='impact-page-open-stat-label'>maatschappelijke waarde uit verkopen</p>
                </div>
            </div>
        </section>
    )

    // Methodiek § 6.3 (#344): elke publicatie zegt hoeveel metingen niet zijn
    // doorgerekend. Een publicatie van vóór die telling zegt niets.
    const coverageNote = organisation.notAttributedCount > 0
        ? <p className='impact-page-open-coverage'>{notAttributedText(organisation.notAttributedCount, organisation.notAttributedReasons)}</p>
        : null

    // Embed-modus: compacte widget zonder header/footer — alleen naam + cijfers.
    if (isEmbed) {
        return (
            <div className='impact-page-open impact-page-open--embed'>
                <section className='impact-page-open-section'>
                    <h2>{branding.name}</h2>
                    <div className='impact-page-open-stats'>
                        <div className='impact-page-open-stat'>
                            <p className='impact-page-open-stat-value' style={{ color: branding.primaryColor || '#47acc3' }}>
                                {organisation.kengetal !== null ? formatEuro(organisation.kengetal) : '—'}
                            </p>
                            <p className='impact-page-open-stat-label'>maatschappelijke waarde per bestede euro</p>
                        </div>
                        <div className='impact-page-open-stat'>
                            <p className='impact-page-open-stat-value' style={{ color: branding.primaryColor || '#47acc3' }}>
                                {organisation.totalValue !== null ? formatEuro(organisation.totalValue) : '—'}
                            </p>
                            <p className='impact-page-open-stat-label'>totale maatschappelijke waarde</p>
                        </div>
                    </div>
                    {coverageNote}
                </section>
                {liveSection}
            </div>
        )
    }

    return (
        <div className='impact-page-open'>
            <header
                className='impact-page-open-header'
                style={{ backgroundColor: branding.secundairyColor || '#206c7f' }}
            >
                {branding.logo && <img src={branding.logo} alt='Logo' />}
                <h1>{branding.name}</h1>
            </header>

            {product && (
                <section className='impact-page-open-section'>
                    <h2>{resolveProductOpening({ Name: product.name, OpeningLine: product.openingLine })}</h2>
                    {product.impact !== null && (
                        <p className='impact-page-open-highlight'>
                            Jouw {product.name} = {formatEuro(product.impact)} aan maatschappelijke waarde
                        </p>
                    )}
                </section>
            )}

            {product && product.microStory && (
                <section className='impact-page-open-section'>
                    <p>{product.microStory}</p>
                </section>
            )}

            {liveSection}

            <section className='impact-page-open-section'>
                <h2>{`Wat ${branding.name || 'deze organisatie'}${organisation.jaar ? ` in ${organisation.jaar}` : ''} bereikte`}</h2>
                <div className='impact-page-open-stats'>
                    <div className='impact-page-open-stat'>
                        <p className='impact-page-open-stat-value' style={{ color: branding.primaryColor || '#47acc3' }}>
                            {organisation.kengetal !== null ? formatEuro(organisation.kengetal) : '—'}
                        </p>
                        <p className='impact-page-open-stat-label'>maatschappelijke waarde per bestede euro</p>
                    </div>
                    <div className='impact-page-open-stat'>
                        <p className='impact-page-open-stat-value' style={{ color: branding.primaryColor || '#47acc3' }}>
                            {organisation.totalValue !== null ? formatEuro(organisation.totalValue) : '—'}
                        </p>
                        <p className='impact-page-open-stat-label'>totale maatschappelijke waarde</p>
                    </div>
                </div>
            </section>

            <footer className='impact-page-open-footer'>
                {publishedAtMs && (
                    <p>Cijfers per {new Date(publishedAtMs).toLocaleDateString('nl-NL')}{organisation.label ? ` · ${organisation.label}` : ''}</p>
                )}
                {coverageNote}
            </footer>
        </div>
    )
}

export default ImpactPageOpen
